"use client";

import { useState } from "react";

import type { Project, ProjectUpdate } from "@/lib/api/types";

type Props = {
  project: Project;
  saving: boolean;
  message: { kind: "ok" | "error"; text: string } | null;
  onSave: (update: ProjectUpdate) => void;
};

const control =
  "h-11 w-full rounded-md border border-field bg-white px-3 text-sm text-ink " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

const MARKETS = [
  { value: "DE", label: "Deutschland · google.de" },
  { value: "AT", label: "Österreich · google.at" },
  { value: "CH", label: "Schweiz · google.ch" },
];
const LANGUAGES = [
  { value: "de", label: "Deutsch (de)" },
  { value: "en", label: "Englisch (en)" },
];

export function ProjectDefaults({ project, saving, message, onSave }: Props) {
  const [country, setCountry] = useState(project.country);
  const [language, setLanguage] = useState(project.language);
  const [jobLimit, setJobLimit] = useState(String(project.cost_limit_per_job_eur));
  const [budget, setBudget] = useState(String(project.monthly_budget_eur));
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const limit = Number(jobLimit.replace(",", "."));
    const monthly = Number(budget.replace(",", "."));
    if (!Number.isFinite(limit) || limit < 0 || !Number.isFinite(monthly) || monthly < 0) {
      setError("Kostenlimit und Monatsbudget müssen Zahlen von 0 an sein.");
      return;
    }
    setError(null);
    onSave({ country, language, cost_limit_per_job_eur: limit, monthly_budget_eur: monthly } as ProjectUpdate);
  };

  const shown = error ? { kind: "error" as const, text: error } : message;

  return (
    <section
      aria-labelledby="defaults-title"
      className="flex flex-col gap-4 rounded-lg border border-line bg-white px-5 py-[18px]"
    >
      <h2 id="defaults-title" className="text-base font-semibold">
        Projekt-Defaults &amp; Kostenlimits
      </h2>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="defaults-country" className="text-[13px] font-medium">
            Markt
          </label>
          <select
            id="defaults-country"
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            className={control}
          >
            {MARKETS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="defaults-language" className="text-[13px] font-medium">
            Sprache
          </label>
          <select
            id="defaults-language"
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            className={control}
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="defaults-limit" className="text-[13px] font-medium">
            Kostenlimit pro Job (€)
          </label>
          <input
            id="defaults-limit"
            type="text"
            inputMode="decimal"
            value={jobLimit}
            onChange={(event) => setJobLimit(event.target.value)}
            className={`${control} font-mono`}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="defaults-budget" className="text-[13px] font-medium">
            Monatsbudget gesamt (€)
          </label>
          <input
            id="defaults-budget"
            type="text"
            inputMode="decimal"
            value={budget}
            onChange={(event) => setBudget(event.target.value)}
            className={`${control} font-mono`}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
        >
          Speichern
        </button>
        <span
          aria-live="polite"
          className={`text-[13px] ${shown?.kind === "error" ? "text-danger" : "text-success"}`}
        >
          {shown?.text}
        </span>
      </div>
      <span className="text-xs text-muted">
        Jobs über dem Limit pausieren vor dem Start und brauchen eine manuelle Freigabe.
      </span>
    </section>
  );
}
